import React from "react";
import { colors, fonts } from "../theme";
import { values, assurances, contact } from "../data";
import { Eyebrow, AssuranceCard, ValuesGrid, assuranceGrid } from "../ui";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import { webPageSchema, breadcrumbSchema } from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
];

export default function About() {
  return (
    <main
      className="se-page"
      style={{
        ...wrap,
        padding: "clamp(36px,8vw,60px) var(--sp-gutter) clamp(48px,11vw,84px)",
      }}
    >
      <Seo
        title={PAGE_META.about.title}
        description={PAGE_META.about.description}
        path="/about"
        image="/assets/stock.webp"
        imageAlt="Sigma Enterprises paper and board stock, New Delhi"
        schema={[
          webPageSchema({
            path: "/about",
            name: PAGE_META.about.title,
            description: PAGE_META.about.description,
            type: "AboutPage",
            primaryImage: "/assets/stock.webp",
            breadcrumbId: "https://sigmapapermart.com/about#breadcrumb",
          }),
          breadcrumbSchema(CRUMBS, "/about"),
        ]}
      />

      <Eyebrow>About Sigma Enterprises</Eyebrow>
      <h1 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h1)", fontWeight: 500, margin: "12px 0 0", maxWidth: "22ch" }}>
        A dependable paper partner for the trade
      </h1>

      <div
        className="se-about2"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "56px", marginTop: "36px", alignItems: "start" }}
      >
        <div style={{ fontSize: "var(--fs-body)", lineHeight: 1.7, color: colors.ink3 }}>
          <p style={{ margin: "0 0 18px" }}>
            Sigma Enterprises has supplied paper and board from New Delhi{" "}
            <strong style={{ fontWeight: 600 }}>since 1996</strong> — three
            decades serving printers, packaging converters, publishers and
            resellers across India from our premises in Ramesh Nagar.
          </p>
          <p style={{ margin: "0 0 18px" }}>
            We hold a broad, ready inventory — from everyday copier and printing paper to art card, kraft board and
            specialty grades — so our customers can source what they need from a single, reliable supplier.
          </p>
          <p style={{ margin: "0 0 18px" }}>
            That range matters more than it sounds. A converter running a carton
            job rarely needs one grade; they need duplex for the carton, kraft
            for the outer, and an adhesive face stock for the label. Sourcing
            those from three suppliers means three lead times and three chances
            to be let down. We carry them under one roof.
          </p>
          <p style={{ margin: 0 }}>
            Our focus is simple: consistent quality, honest trade pricing and delivery you can plan around.
          </p>
        </div>
        <div
          style={{
            aspectRatio: "1/1",
            border: `1px solid ${colors.border}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {/* Sits beside the opening paragraphs, so it is above the fold and
              is this page's LCP candidate: eager + high fetch priority, never
              lazy. width/height match the 1:1 frame it renders in, which lets
              the browser reserve the box before the file arrives. */}
          <img
            src="/assets/stock.webp"
            alt="Bundles of paper and board strapped and stacked in the Sigma Enterprises warehouse"
            width={560}
            height={560}
            loading="eager"
            fetchpriority="high"
            decoding="sync"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
      </div>

      <ValuesGrid values={values} style={{ marginTop: "56px" }} />

      {/* The one heading on the page that was not clamped, so at 320px it came
          out the same size as the h1 above it and the hierarchy read flat.
          --fs-stat keeps the 30px desktop end and drops to 24px on a phone.
          The 60px top margin is fluid for the same reason. */}
      <h2 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-stat)", fontWeight: 500, margin: "clamp(40px,9vw,60px) 0 8px" }}>
        What buyers get
      </h2>
      <p style={{ fontSize: "var(--fs-body)", color: colors.muted, maxWidth: "60ch", margin: "0 0 26px" }}>
        What the printers and converters we supply can rely on.
      </p>
      <div style={assuranceGrid}>
        {assurances.map((item) => (
          <AssuranceCard key={item.t} item={item} />
        ))}
      </div>
      <p style={{ fontSize: "var(--fs-body)", color: colors.muted, margin: "26px 0 0" }}>
        Customer reviews live on our{" "}
        {/* 39px tall as a bare inline run. The padding lifts it over 44 and
            the negative margin puts the sentence's line rhythm back. The
            underline moves to box-shadow because a border-bottom on an inline
            box redraws on every wrapped fragment, and this label does wrap on
            a narrow screen. */}
        <a
          href={contact.mapLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: colors.brown,
            display: "inline-block",
            padding: "13px 2px",
            margin: "-13px -2px",
            boxShadow: "inset 0 -1px 0 #C9B896",
          }}
        >
          Google Business Profile
        </a>
        , where they are verified and public.
      </p>
    </main>
  );
}
